import { faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, Table, TableColumn } from '@vadiun/react-components';
import { PassengerModel } from 'app/client/clientBusiness/Passenger/models/Passenger';
import { DomicilioModel } from 'app/domicilio/models/DomicilioModel';
import { ViajeTrasladoEstado } from 'app/viaje/models';
import LocationDetails from 'app/viaje/viajeTraslado/viajeTraslado/components/LocationDetails';
import { TransferReservationPartialModel } from 'app/viaje/viajeTraslado/viajeTraslado/models/TransferReservationPartial';
import PageContainer from 'layout/components/PageContainer';
import PageContainerMobile from 'layout/components/PageContainerMobile';
import PageHeader from 'layout/components/PageHeader';
import { useState } from 'react';
import { useViewport } from 'shared/hooks/useViewport';
import { createDomicilioText } from 'shared/models/DomicilioGoogle';
import { DriverPendingSettlementsFiltersComponent } from '../components/DriverPendingSettlementsFiltersComponent';
import { ResumenPreciosViajesComponent } from '../components/ResumenPreciosViajesComponent';
import { DriverTransferReservationsWithPrices } from '../models/DriverTransferReservationsWithSettlementInfo';
import DriverPendingSettlementMobile from './DriverPendingSettlementMobile';
import {
  useDriverSettlements,
  settlementsColumnLabels
} from '../hooks/useDriverSettlements';
import RefreshList from 'shared/components/Table/Refresh';
import ColumnSelector from 'shared/components/Table/actions-buttons/ColumnSelector';

export interface IParsedInfo
  extends Omit<DriverTransferReservationsWithPrices, 'client'> {
  client: string;
  locations: {
    observation: string;
    minutesWaiting: number;
    address: DomicilioModel;
    passengers: PassengerModel[];
  }[];
  status: ViajeTrasladoEstado;
  dateTime: string;
}

export const DriverTransferReservationPendingSettlement = () => {
  const {
    settlements,
    reload,
    filters,
    onFiltersChange,
    columnVisibility,
    setColumnVisibility
  } = useDriverSettlements();

  const { breaks } = useViewport(720);

  const [locationsDetail, setLocationsDetail] = useState<{
    isOpen: boolean;
    details?: {
      address: DomicilioModel;
      observation: string;
    }[];
  }>({ isOpen: false, details: undefined });

  const onLocationDetails = (
    locations: TransferReservationPartialModel['locations']
  ) => {
    setLocationsDetail({
      isOpen: true,
      details: locations.map((l) => ({
        address: l.address,
        observation: l.observation
      }))
    });
  };

  const data = (settlements.data ?? []).map((d) => {
    return {
      ...d,
      client: d.client.name,
      locations: d.locations,
      status: d.state,
      dateTime: d.reservationDatetime.format('DD/MM/YYYY HH:mm [hs.]')
    };
  });

  const columns: TableColumn<typeof data[0]>[] = [
    { name: 'id', label: 'ID', options: { display: columnVisibility.id } },
    {
      name: 'dateTime',
      label: 'Fecha',
      options: { display: columnVisibility.dateTime }
    },
    {
      name: 'client',
      label: 'Cliente',
      options: { display: columnVisibility.client }
    },
    {
      name: 'locations',
      label: 'Recorrido',
      options: {
        display: columnVisibility.locations,
        customBodyRenderLite: (index) => {
          const firstPoint = data[index].locations[0];
          const lastPoint =
            data[index].locations[data[index].locations.length - 1];
          return (
            <div className="flex flex-col items-center">
              <span className="title text-center text-xs">
                {createDomicilioText(firstPoint.address)}
              </span>
              <Button
                shape="circle"
                className="p-2"
                onClick={() => onLocationDetails(data[index].locations)}
                color={data[index].locations.length > 2 ? 'blue' : 'gray'}
              >
                <div className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faArrowDown} />
                  {data[index].locations.length > 2
                    ? data[index].locations.length
                    : null}
                </div>
              </Button>

              <span className="title text-center text-xs">
                {createDomicilioText(lastPoint.address)}
              </span>
            </div>
          );
        }
      }
    },
    {
      name: 'state',
      label: 'Estado',
      options: { display: columnVisibility.state }
    },
    {
      name: 'contado',
      label: 'Contado',
      options: { display: columnVisibility.contado }
    },
    {
      name: 'cuenta_corriente',
      label: 'Cuenta Corriente',
      options: { display: columnVisibility.cuenta_corriente }
    },
    {
      name: 'total_precio_espera',
      label: 'Espera',
      options: { display: columnVisibility.total_precio_espera }
    },
    {
      name: 'total_gastos',
      label: 'Gastos',
      options: {
        display: columnVisibility.total_gastos,
        customBodyRenderLite: (index) => {
          return (
            <div
              className="flex items-center justify-center h-full border-2 py-1 px-2 rounded-lg "
              style={{
                borderColor: '#f0f0f0',
                color: '#FF0000',
                background:
                  'linear-gradient(to right, #FFFFFF, rgba(255, 0, 0, 0.04))'
              }}
            >
              ${data[index].total_gastos}
            </div>
          );
        }
      }
    },
    {
      name: 'total_a_facturar',
      label: 'Total',
      options: { display: columnVisibility.total_a_facturar }
    }
  ];

  const Resume =
    settlements.resumen && settlements.data.length > 0 ? (
      <ResumenPreciosViajesComponent resumen={settlements.resumen} />
    ) : null;

  const Filter = (
    <DriverPendingSettlementsFiltersComponent
      onSubmit={async (values) => {
        onFiltersChange(values);
      }}
      filters={filters}
    />
  );
  return (
    <>
      <PageHeader title="Pendientes de liquidación" />
      <div
        className="sticky top-0 right-0 left-0 flex flex-col"
        style={{
          zIndex: 101,
          background: '#EAEDF5',
          marginBottom: '-24px'
        }}
      >
        <div className="flex w-full md:px-8 mt-2">
          <div
            className="flex justify-between items-center px-2 w-full rounded-t-lg"
            style={{
              backgroundColor: '#00163B',
              paddingTop: 4,
              paddingBottom: 4
            }}
          >
            {Filter}
            <div className="flex justify-end py-1 md:px-4 ml-4 w-fit">
              <RefreshList
                onRefreshTable={() => reload()}
                isRefreshing={settlements.isLoading}
              />
              <ColumnSelector
                columns={columnVisibility}
                onChange={setColumnVisibility}
                columnLabels={settlementsColumnLabels}
              />
            </div>
          </div>
        </div>
      </div>
      {!breaks ? (
        <PageContainer lessMargin>
          <div className="flex flex-col w-full mt-4">
            {Resume}
            <Table
              columns={columns}
              data={data}
              title={undefined}
              options={{
                responsive: 'standard',
                filter: false,
                search: false,
                print: false,
                download: false,
                viewColumns: false
              }}
            />
          </div>
          <Modal
            open={locationsDetail.isOpen}
            onClose={() =>
              setLocationsDetail({ isOpen: false, details: undefined })
            }
            size="md"
            body={
              <div style={{ height: 300 }}>
                <LocationDetails
                  locations={(locationsDetail.details ?? []).map((l) => ({
                    ...l,
                    passengers: []
                  }))}
                />
              </div>
            }
          />
        </PageContainer>
      ) : (
        <PageContainerMobile>
          <div className="flex flex-col w-full mt-4">
            {Resume}
            <DriverPendingSettlementMobile
              data={settlements.data ?? []}
              isLoading={settlements.isLoading}
              columnVisibility={columnVisibility}
            />
          </div>
        </PageContainerMobile>
      )}
    </>
  );
};
